import { Button, Input, Modal, ModalBody, ModalHeader } from 'reactstrap';
import { useState } from 'react';
import { ColumnDef, Table } from '@g17eco/molecules/table';
import {
  useGetUtrValueListCustomOptionsQuery,
  UtrValueListCustomOptionsResponse,
} from '@api/initiative-universal-trackers';
import { BlockingLoader } from '@g17eco/atoms/loader';
import { MergedValueListOption } from '@g17eco/types/initiativeUniversalTracker';
import { isDuplicatedOption } from './utils';
import { normalizeText } from '@utils/string-format';

interface Props {
  isOpen: boolean;
  toggle: () => void;
  initiativeId: string;
  utrId: string;
  columnCode?: string;
  options: MergedValueListOption[];
  updateOptions: (newOptions: MergedValueListOption[]) => void;
}

export const UserAddedCustomOptionsModal = ({
  isOpen,
  toggle,
  initiativeId,
  utrId,
  columnCode,
  options,
  updateOptions,
}: Props) => {
  const [search, setSearch] = useState('');
  const { data, isFetching } = useGetUtrValueListCustomOptionsQuery(
    { initiativeId, utrId, columnCode },
    {
      skip: !isOpen,
    },
  );

  const filteredData = data?.filter(
    (customOption) =>
      normalizeText(customOption.name).includes(normalizeText(search)) &&
      !isDuplicatedOption(options, {
        code: normalizeText(customOption.name),
        name: customOption.name,
      }),
  );

  const handleAddOption = (option: string) => {
    const code = normalizeText(option); // To be compatible with user added custom options so won't break aggregation.
    const isDuplicated = isDuplicatedOption(options, { code, name: option });
    if (isDuplicated) {
      return;
    }

    const newOptions = [...options, { code, name: normalizeText(option, { lowerCase: false }), isCustom: true }]; // Keep case sensitive name.
    updateOptions(newOptions);
  };

  const tableColumns: ColumnDef<UtrValueListCustomOptionsResponse>[] = [
    {
      header: 'Option title',
      accessorKey: 'name',
    },
    {
      header: 'Uses',
      accessorKey: 'usage.utrvIds.length',
    },
    {
      header: 'Subsidiary',
      accessorKey: 'usage.initiativeIds.length',
    },
    {
      id: 'selectBtn',
      header: '',
      cell: (c) => {
        return (
          <div className='d-flex justify-content-end'>
            <Button color='secondary' size='sm' onClick={() => handleAddOption(c.row.original.name)}>
              Add option
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <Modal isOpen={isOpen} toggle={toggle} backdrop='static'>
      <ModalHeader toggle={toggle}>User added custom options</ModalHeader>
      <ModalBody>
        {isFetching ? <BlockingLoader /> : null}
        <p>
          Listed below are the custom options contributors have added for this dropdown. Once added to the official list
          for all users, the option will be hidden from this table.
        </p>
        <Input
          className='w-100 mb-3'
          placeholder='Search for option...'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Table columns={tableColumns} data={filteredData ?? []} pageSize={10} />
        {!filteredData?.length ? <p className='mt-2 mb-0'>No custom options found</p> : null}
      </ModalBody>
    </Modal>
  );
};
